/**
 * SketchShape - Abstract Base Class for 2D Shapes
 * Ported from cc.sketchchair.sketch.SketchShape.java
 *
 * Base class for all 2D shapes in the sketch engine.
 * Provides common interface for paths, splines, and other shapes.
 */

export class SketchShape {
    // Shape type constants
    static TYPE_PATH = 2;
    static TYPE_SPLINE = 3;
    static OFFSET_SPLINE = 4;

    // Union operation constants
    static UNION_ADD = 0;
    static UNION_SUBTRACT = 1;

    /**
     * Create a new sketch shape
     * @param {Object} parentSketch - Parent sketch object
     */
    constructor(parentSketch = null) {
        this.parentSketch = parentSketch;
        this.type = 0;
        this.id = 0;
        this.destroy = false;
        this.selected = false;
        this.editable = true;
        this.isSelectedShape = false;
        this.closed = false;
        this.highlighted = false;
        this.isConstructionLine = false;
        this.union = SketchShape.UNION_ADD;

        // UI state
        this.lastMouseOverPercent = 0.0;
        this.lastMouseOverVec = null;
        this.debugPercent = 0;

        // Selection
        this.selectedNodes = [];

        // Assign ID from parent sketch if available
        if (this.parentSketch && this.parentSketch.sketchGlobals) {
            this.id = this.parentSketch.sketchGlobals.sketch_id++;
        }
    }

    // ============================================
    // Abstract Methods (to be implemented by subclasses)
    // ============================================

    /**
     * Add a point to the shape
     * @param {SketchPoint} point - Point to add
     * @abstract
     */
    add(point) {
        throw new Error('add() must be implemented by subclass');
    }

    /**
     * Build/rebuild the shape geometry
     * @abstract
     */
    build() {
        throw new Error('build() must be implemented by subclass');
    }

    /**
     * Clone the shape
     * @returns {SketchShape} Cloned shape
     * @abstract
     */
    clone() {
        throw new Error('clone() must be implemented by subclass');
    }

    /**
     * Copy the shape with new parent
     * @param {Object} parentSketch - New parent sketch
     * @returns {SketchShape} Copied shape
     * @abstract
     */
    copy(parentSketch) {
        throw new Error('copy() must be implemented by subclass');
    }

    /**
     * Destroy/cleanup the shape
     * @abstract
     */
    destroy() {
        throw new Error('destroy() must be implemented by subclass');
    }

    /**
     * Get closest percent along path
     * @param {number} x - X coordinate
     * @param {number} y - Y coordinate
     * @returns {number} Percent (0-1)
     * @abstract
     */
    getClosestPercent(x, y) {
        throw new Error('getClosestPercent() must be implemented by subclass');
    }

    /**
     * Get closest point on shape
     * @param {Vec2D|SketchPoint} point - Point to check
     * @returns {SketchPoint} Closest point
     * @abstract
     */
    getClosestPoint(point) {
        throw new Error('getClosestPoint() must be implemented by subclass');
    }

    /**
     * Get position at percent along path
     * @param {number} percent - Percent (0-1)
     * @returns {Vec2D} Position
     * @abstract
     */
    getPos(percent) {
        throw new Error('getPos() must be implemented by subclass');
    }

    /**
     * Get perpendicular vector at percent
     * @param {number} percent - Percent (0-1)
     * @returns {Vec2D} Perpendicular vector
     * @abstract
     */
    getPerpendicular(percent) {
        throw new Error('getPerpendicular() must be implemented by subclass');
    }

    /**
     * Get the path representation
     * @returns {SketchPath} Path
     * @abstract
     */
    getPath() {
        throw new Error('getPath() must be implemented by subclass');
    }

    /**
     * Get length of shape
     * @returns {number} Length
     * @abstract
     */
    getLength() {
        throw new Error('getLength() must be implemented by subclass');
    }

    /**
     * Render the shape
     * @param {CanvasRenderingContext2D} ctx - Canvas context
     * @abstract
     */
    render(ctx) {
        throw new Error('render() must be implemented by subclass');
    }

    /**
     * Check if point is inside shape
     * @param {Vec2D} point - Point to check
     * @returns {boolean} True if inside
     * @abstract
     */
    isPointInside(point) {
        throw new Error('isPointInside() must be implemented by subclass');
    }

    /**
     * Convert to JSON
     * @returns {Object} JSON representation
     * @abstract
     */
    toJSON() {
        throw new Error('toJSON() must be implemented by subclass');
    }

    // ============================================
    // Concrete Methods
    // ============================================

    /**
     * Get parent sketch
     * @returns {Object} Parent sketch
     */
    getParentSketch() {
        return this.parentSketch;
    }

    /**
     * Set parent sketch
     * @param {Object} sketch - Parent sketch
     */
    setParentSketch(sketch) {
        this.parentSketch = sketch;
    }

    /**
     * Get shape ID
     * @returns {number} ID
     */
    getId() {
        return this.id;
    }

    /**
     * Set shape ID
     * @param {number} id - New ID
     */
    setId(id) {
        this.id = id;
    }

    /**
     * Get shape type
     * @returns {number} Type constant
     */
    getType() {
        return this.type;
    }

    /**
     * Set shape type
     * @param {number} type - Type constant
     */
    setType(type) {
        this.type = type;
    }

    /**
     * Check if closed
     * @returns {boolean} True if closed
     */
    getClosed() {
        return this.closed;
    }

    /**
     * Set closed state
     * @param {boolean} flag - Closed state
     */
    setClosed(flag) {
        this.closed = flag;
    }

    /**
     * Check if construction line
     * @returns {boolean} True if construction line
     */
    isConstructionLine() {
        return this.isConstructionLine;
    }

    /**
     * Set construction line state
     * @param {boolean} construction - Construction line state
     */
    setIsConstructionLine(construction) {
        this.isConstructionLine = construction;
    }

    /**
     * Check if marked for destruction
     * @returns {boolean} True if destroying
     */
    isDestroying() {
        return this.destroy;
    }

    /**
     * Mark for destruction
     * @param {boolean} destroy - Destroy state
     */
    setDestroy(destroy) {
        this.destroy = destroy;
    }

    /**
     * Set editable state
     * @param {boolean} editable - Editable state
     */
    setEditable(editable) {
        this.editable = editable;
    }

    /**
     * Highlight shape
     */
    highlight() {
        this.highlighted = true;
    }

    /**
     * Unhighlight shape
     */
    unhighlight() {
        this.highlighted = false;
    }

    /**
     * Select shape
     */
    select() {
        this.selected = true;
    }

    /**
     * Unselect shape
     */
    unselect() {
        this.selected = false;
    }

    /**
     * Get selected nodes
     * @returns {Array} Selected nodes
     */
    getSelectedNodes() {
        return this.selectedNodes;
    }

    /**
     * Set selected nodes
     * @param {Array} nodes - Selected nodes
     */
    setSelectedNodes(nodes) {
        this.selectedNodes = nodes;
    }

    /**
     * Count selected nodes
     * @returns {number} Count
     */
    countSelectedNodes() {
        return this.selectedNodes.length;
    }

    /**
     * Unselect all nodes
     */
    unselectNodes() {
        this.selectedNodes = [];
    }

    /**
     * Check if overlaps another shape
     * @param {SketchShape} otherShape - Shape to check
     * @returns {boolean} True if overlaps
     */
    overlaps(otherShape) {
        const path1 = this.getPath();
        const path2 = otherShape.getPath();
        if (path1 && path2) {
            return path1.intersects(path2);
        }
        return false;
    }

    /**
     * Get size (default implementation)
     * @returns {number} Size
     */
    size() {
        return 0;
    }

    /**
     * Get point at index (default implementation)
     * @param {number} i - Index
     * @returns {SketchPoint|null} Point
     */
    get(i) {
        return null;
    }

    /**
     * Get last point (default implementation)
     * @returns {SketchPoint|null} Last point
     */
    getLast() {
        return null;
    }
}
